import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { unwrapList } from '../../utils/format.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'

/**
 * Patient-facing imaging marketplace.
 *
 * Search the global imaging-study catalogue → expand to see every
 * radiology centre that offers it, with price + turnaround time. Like
 * the lab browser, imaging studies are ordered through a doctor; the
 * page exists so a patient can compare options before consulting.
 */
export default function PatientImagingBrowser() {
  const [q, setQ] = useState('')
  const [modality, setModality] = useState('')
  const debounced = useDebounce(q, 250)
  const [openId, setOpenId] = useState(null)

  // Match the modality vocabulary used elsewhere in the app
  // (RadiologyCatalogue downloader, LabDashboard chips, etc.).
  const MODALITIES = [
    ['', 'All modalities'],
    ['XR', 'X-ray (XR)'],
    ['US', 'Ultrasound (US)'],
    ['CT', 'CT'],
    ['MRI', 'MRI'],
    ['DEXA', 'DEXA'],
    ['MAMMO', 'Mammography'],
    ['FLUORO', 'Fluoroscopy'],
    ['ANGIO', 'Angiography'],
    ['NM', 'Nuclear medicine'],
    ['PET', 'PET'],
    ['ECHO', 'Echocardiography'],
  ]

  const studiesQ = useQuery({
    queryKey: ['imaging-studies', debounced, modality],
    queryFn: async () => {
      const params = {}
      if (debounced.trim().length >= 2) params.search = debounced.trim()
      if (modality) params.modality = modality
      const { data } = await api.get(endpoints.imagingStudySearch, { params })
      return unwrapList(data)
    },
    enabled: debounced.trim().length >= 2 || debounced === '' || Boolean(modality),
    staleTime: 60_000,
  })
  const rows = studiesQ.data || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Imaging marketplace"
          subtitle="Browse imaging studies (X-ray, ultrasound, CT, MRI, …) the network's radiology centres offer. Pick a study to see who runs it, how much it costs, and how long results take."
          right={
            <Link to="/patient" className="text-xs text-wantok-700 underline">
              ← Back to my home
            </Link>
          }
        />
        <div className="flex flex-wrap gap-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Search exam name (e.g. "chest x-ray", "abdominal US", "brain CT")…'
            className="flex-1 min-w-[14rem] border rounded px-3 py-2 text-sm"
          />
          <select
            value={modality}
            onChange={(e) => setModality(e.target.value)}
            className="border rounded px-3 py-2 text-sm bg-white"
          >
            {MODALITIES.map(([v, l]) => (
              <option key={v || 'all'} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </Card>

      {studiesQ.error && <ErrorBanner error={studiesQ.error} onRetry={() => studiesQ.refetch()} />}

      {studiesQ.isLoading && (
        <div className="text-sm text-slate-400 text-center py-6">Searching…</div>
      )}

      {!studiesQ.isLoading && debounced.trim().length === 0 && !modality && (
        <Card>
          <div className="text-sm text-slate-500">
            Type an exam name above (or pick a modality) to see what's
            offered and where. Imaging studies are ordered through your
            doctor.
          </div>
        </Card>
      )}

      {!studiesQ.isLoading && (debounced.trim().length > 0 || modality) && rows.length === 0 && (
        <Card>
          <div className="text-sm text-slate-500">
            No imaging studies match your filters. Try broader keywords or
            a different modality.
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((s) => (
          <ImagingStudyRow
            key={s.id}
            study={s}
            expanded={openId === s.id}
            onToggle={() => setOpenId(openId === s.id ? null : s.id)}
          />
        ))}
      </div>
    </div>
  )
}

function ImagingStudyRow({ study, expanded, onToggle }) {
  return (
    <Card>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <CatalogueImageThumb url={study.image_url} alt={study.exam_name} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">
              {study.exam_name}
              {study.short_code && (
                <span className="ml-2 text-[10px] uppercase text-slate-500 font-mono">
                  {study.short_code}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-2">
              <span className="font-medium text-slate-700">{study.modality_display || study.modality}</span>
              {study.body_region && <span>· {study.body_region}</span>}
              {study.contrast && (
                <span className="text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-1.5 py-0.5 text-[10px] uppercase">contrast</span>
              )}
              {study.requires_appointment && (
                <span className="text-slate-600 border border-slate-200 bg-slate-50 rounded-full px-1.5 py-0.5 text-[10px] uppercase">appointment</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {study.indicative_price_pgk != null && (
            <span className="text-[11px] text-slate-500">
              from PGK {study.indicative_price_pgk}
            </span>
          )}
          <span className="text-wantok-700 text-sm">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </button>
      {expanded && <ImagingAvailability studyId={study.id} examName={study.exam_name} />}
    </Card>
  )
}

function ImagingAvailability({ studyId, examName }) {
  const stockQ = useQuery({
    queryKey: ['imaging-study-stock', studyId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.imagingStudySearch}${studyId}/stock/`)
      return data
    },
    staleTime: 30_000,
  })
  const rows = stockQ.data || []

  if (stockQ.isLoading) {
    return <div className="mt-3 text-sm text-slate-400">Loading availability…</div>
  }
  if (stockQ.error) {
    return <div className="mt-3"><ErrorBanner error={stockQ.error} /></div>
  }
  if (rows.length === 0) {
    return (
      <div className="mt-3 text-sm text-slate-500 italic">
        No radiology centre in the network is currently offering <b>{examName}</b>.
        Your doctor may suggest an alternative — book a consultation to ask.
      </div>
    )
  }

  const sorted = [...rows].sort((a, b) => {
    const ap = a.price ?? Infinity
    const bp = b.price ?? Infinity
    return ap - bp
  })

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
        {sorted.length} centre{sorted.length === 1 ? '' : 's'} offering this — cheapest first
      </div>
      <ul className="divide-y divide-slate-100">
        {sorted.map((row) => (
          <li key={row.id} className="py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CatalogueImageThumb
                url={row.radiology_center_logo_url}
                alt={row.radiology_center_name}
                size={32}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {row.radiology_center_name}
                </div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                  {row.turnaround_time && <span>TAT {row.turnaround_time}</span>}
                  {row.requires_appointment && <span>· Appointment required</span>}
                  {row.contrast && <span>· Contrast</span>}
                  {row.notes && <span className="truncate">· {row.notes}</span>}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-slate-800">
                {row.price != null ? `PGK ${row.price}` : '—'}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Imaging studies are ordered through your doctor.</span>
        <Link to="/patient/book" className="text-wantok-700 underline">
          Book a consultation →
        </Link>
      </div>
    </div>
  )
}
